import React, { FC, useEffect, useMemo, useState } from 'react'
import Helmet from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { fetchSettingBaseWorkTime, updateSettingBaseWorkTime } from '../../../../states/containers/setting/systemSlice'
import { Link, useParams } from 'react-router-dom'
import { setPath } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import { LangValue } from '../../../../states/types/common'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { RequestPatchSettingBaseWorkTime, SettingBaseWorkTimeMember } from '../../../../states/types/setting'
import LoadingSquare from '../../../components/common/LoadingSquare'
import { useAlert } from 'react-alert'
import { secToHour, secToMinute, secToTwoDigitsHourMinute } from '../../../../utils/date'
import SettingBaseWorkTimeModal from '../../../components/common/SettingBaseWorkTimeModal'
import { pickNumber } from '../../../../utils/string'
import BaseWorkTimeMemberListModule from '../../../components/common/BaseWorkTimeMemberListModule'
import { RootState } from '../../../../states/containers/store'

interface Error {
  type: string
  message: string
  target?: string[]
}

interface CommonValue {
  base_work_start_time: string | null
  base_work_end_time: string | null
  base_break_time: number
  break_times: {
    start_time: string
    end_time: string
  }[]
}

interface WorkTime {
  use_common_setting: boolean
  base_work_start_time?: string | null
  base_work_end_time?: string | null
  base_break_time?: number
}

interface BreakTime {
  use_common_setting: boolean
  break_times?: {
    start_time: string
    end_time: string
  }[]
}

const SELECT_INTERVAL_WORK_TIME_MINUTES = 5

const SELECT_INTERVAL_BREAK_TIME_MINUTES = 5

const BREAK_TIME_LIMIT = 5

const Index: FC = () => {
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const {
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [originMemberList, setOriginMemberList] = useState<SettingBaseWorkTimeMember[]>([])
  const commonValueInit = {
    base_work_start_time: "",
    base_work_end_time: "",
    base_break_time: 0,
    break_times: [],
  }
  const [commonValue, setCommonValue] = useState<CommonValue>(commonValueInit)

  const [isDisplayModal, setIsDisplayModal] = useState<boolean>(false)
  const [targetMembers, setTargetMembers] = useState<SettingBaseWorkTimeMember[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [isBaseWorkTime, setIsBaseWorkTime] = useState<boolean>(false)
  const [baseWorkStartTime, setBaseWorkStartTime] = useState<string>("")
  const [baseWorkEndTime, setBaseWorkEndTime] = useState<string>("")
  const [baseBreakTime, setBaseBreakTime] = useState<number>(0)
  const [isScheduledBreakTimes, setIsScheduledBreakTimes] = useState<boolean>(false)
  const [scheduledBreakTimes, setScheduledBreakTimes] = useState<{start_time: string, end_time: string}[]>([])

  const [errors, setErrors] = useState<Error[]>([])

  const workTimeList = useMemo(() => {
    if (SELECT_INTERVAL_WORK_TIME_MINUTES > 60) return []
    const tmp = Array(1440 / SELECT_INTERVAL_WORK_TIME_MINUTES).fill(null).map((_v, i) => (
      secToTwoDigitsHourMinute(60 * (i * SELECT_INTERVAL_WORK_TIME_MINUTES) + (3600 * 5))
    ))
    return tmp
  }, [])

  const breakTimeList = useMemo(() => {
    if (SELECT_INTERVAL_BREAK_TIME_MINUTES > 60) return []
    const tmp = Array((960 + SELECT_INTERVAL_BREAK_TIME_MINUTES) / SELECT_INTERVAL_BREAK_TIME_MINUTES).fill(null).map((_v, i) => {
      const hours = secToHour(60 * (i * SELECT_INTERVAL_BREAK_TIME_MINUTES))
      const minutes = secToMinute(60 * (i * SELECT_INTERVAL_BREAK_TIME_MINUTES))
      return ({
        key: `${hours ? `${hours}時間` : ""}${minutes ? `${minutes}分` : ""}`,
        value: 60 * (i * SELECT_INTERVAL_BREAK_TIME_MINUTES),
      })
    })
    return tmp
  }, [])

  const workTimeSeconds = useMemo(() => {
    if (!baseWorkStartTime || !baseWorkEndTime) return 0
    const [tmpStartHours, tmpStartMinutes] = baseWorkStartTime.split(":")
    const [tmpEndHours, tmpEndMinutes] = baseWorkEndTime.split(":")
    return ((Number(tmpEndHours) - Number(tmpStartHours)) * 3600) + ((Number(tmpEndMinutes) - Number(tmpStartMinutes)) * 60)
  }, [baseWorkStartTime, baseWorkEndTime])

  const scheduledBreakTimeSeconds = useMemo(() => {
    if (!scheduledBreakTimes.length) return 0
    return scheduledBreakTimes.reduce((s, v) => {
      const [tmpStartHours, tmpStartMinutes] = v.start_time.split(":")
      const [tmpEndHours, tmpEndMinutes] = v.end_time.split(":")
      return s + ((Number(tmpEndHours) - Number(tmpStartHours)) * 3600) + ((Number(tmpEndMinutes) - Number(tmpStartMinutes)) * 60)
    }, 0)
  }, [scheduledBreakTimes])

  const buildSecToTime = (sec: number) => {
    let tmpText = ""
    if (Number(secToHour(sec))) {
      tmpText += `${secToHour(sec)}時間`
    }
    if (Number(secToMinute(sec))) {
      tmpText += `${secToMinute(sec)}分`
    } else {
      tmpText += "0分"
    }
    return tmpText
  }

  const openUpdateModal = (memberIds: number[]) => {
    setTargetMembers(originMemberList.filter((v) => memberIds.includes(v.id)))
    setIsDisplayModal(true)
  }

  const closeModal = () => {
    setTargetMembers([])
    setIsDisplayModal(false)
  }

  const changeValue = (changeCallback: (value: string | number) => void, value: string | number) => {
    changeCallback(value)
  }

  const buildWorkTimeSelectBox = (currentValue: string, changeCallback: (value: number | string) => void) => {
    if (!currentValue) {
      return (<>指定なし</>)
    }
    return (
      <select
        style={{
          border: "solid 1px #CCCCCC",
          borderRadius: "4px",
          padding: "4px",
          backgroundColor: "#FFFFFF",
          appearance: "auto",
        }}
        value={currentValue}
        onChange={(e) => changeValue(changeCallback, e.target.value)}
      >
        {
          workTimeList.map((v) => (
            <option value={v} key={`${v}`}>
              {v || "指定なし"}
            </option>
          ))
        }
      </select>
    )
  }

  const buildBreakTimeSelectBox = (currentValue: number, changeCallback: (value: number | string) => void) => (
    <select
      style={{
        border: "solid 1px #CCCCCC",
        borderRadius: "4px",
        padding: "4px",
        backgroundColor: "#FFFFFF",
        appearance: "auto",
      }}
      value={currentValue}
      onChange={(e) => changeValue(changeCallback, Number(e.target.value))}
    >
      {
        breakTimeList.map((v) => (
          <option value={v.value} key={`break_${v.value}`}>
            {v.key || "指定なし"}
          </option>
        ))
      }
    </select>
  )

  const buildCheckBox = (value: boolean) => (
    value ? (
      <FontAwesomeIcon
        icon={["fas", "check-square"]}
        style={{ color: "#008800" }}
        fixedWidth
      />
    ) : (
      <FontAwesomeIcon
        icon={["far", "square"]}
        style={{ color: "#999999" }}
        fixedWidth
      />
    )
  )

  const timeAdjuster = (time: string | null, to29 = false) => {
    if (to29) {
      if (time) {
        const [hours, minutes] = time.split(":")
        if (Number(hours) >= 5) {
          return `0${time}`.slice(-5)
        }
        return `0${Number(hours) + 24}:${minutes}`.slice(-5)
      }
    }
    if (time) {
      const [hours, minutes] = time.split(":")
      if (Number(hours) < 24) {
        return `0${time}`.slice(-5)
      }
      return `0${Number(hours) - 24}:${minutes}`.slice(-5)
    }
    return null
  }

  const changeSettingBaseWorkTime = () => {
    if (isBaseWorkTime) {
      setBaseWorkStartTime("")
      setBaseWorkEndTime("")
      setBaseBreakTime(0)
    } else {
      setBaseWorkStartTime(timeAdjuster(commonValue?.base_work_start_time || null, true) || "09:00")
      setBaseWorkEndTime(timeAdjuster(commonValue?.base_work_end_time || null, true) || "18:00")
      setBaseBreakTime(commonValue?.base_break_time || 3600)
    }
    setIsBaseWorkTime((v) => !v)
  }

  const changeSettingScheduledBreakTime = () => {
    if (isScheduledBreakTimes) {
      setScheduledBreakTimes([])
    } else {
      setScheduledBreakTimes(
        commonValue?.break_times.length
          ? commonValue?.break_times.map((v) => ({
            start_time: timeAdjuster(v.start_time, true) || "12:00",
            end_time: timeAdjuster(v.end_time, true) || "13:00",
          }))
          : [{ start_time: "12:00", end_time: "13:00" }],
      )
    }
    setIsScheduledBreakTimes((v) => !v)
  }

  const addBreakTime = () => {
    let tmpTime = { start_time: "12:00", end_time: "13:00" }
    if (scheduledBreakTimes.length) {
      const targetBreakTime = scheduledBreakTimes[scheduledBreakTimes.length - 1]
      const [newEndHours, newEndMinutes] = targetBreakTime.end_time.split(':')
      tmpTime = {
        start_time: targetBreakTime.end_time,
        end_time: `${Number(newEndHours) < 28 ? `00${Number(newEndHours) + 1}`.slice(-2) : "28"}:${newEndMinutes}`,
      }
    }
    setScheduledBreakTimes((cur) => [...cur, tmpTime])
  }

  const delBreakTime = (index: number) => {
    if (scheduledBreakTimes.length === 1) {
      setScheduledBreakTimes([])
      setIsScheduledBreakTimes(false)
    } else {
      setScheduledBreakTimes((cur) => cur.filter((_v, i) => i !== index))
    }
  }

  const submit = () => {
    const param: RequestPatchSettingBaseWorkTime = { change_common_setting: true }
    param.work_time = {
      base_work_start_time: timeAdjuster(baseWorkStartTime),
      base_work_end_time: timeAdjuster(baseWorkEndTime),
      base_break_time: baseBreakTime,
    }
    param.break_time = {
      break_times: scheduledBreakTimes.map((v) => ({
        start_time: timeAdjuster(v.start_time) || "12:00",
        end_time: timeAdjuster(v.end_time) || "13:00",
      })) || [],
    }
    dispatch(
      updateSettingBaseWorkTime(
        param,
        () => {
          alert.success("会社情報を変更しました")
          setCommonValue({
            base_work_start_time: timeAdjuster(baseWorkStartTime),
            base_work_end_time: timeAdjuster(baseWorkEndTime),
            base_break_time: baseBreakTime,
            break_times: scheduledBreakTimes,
          })
        },
      ),
    )
  }

  const changeBaseWorkTime = (data: { memberIds: number[], workTime: WorkTime, breakTime: BreakTime }) => {
    const param: RequestPatchSettingBaseWorkTime = { change_common_setting: false, member_ids: data.memberIds }
    if (data.workTime.use_common_setting) {
      param.work_time = {
        use_common_setting: true,
      }
    } else {
      param.work_time = {
        use_common_setting: false,
        base_work_start_time: data.workTime.base_work_start_time,
        base_work_end_time: data.workTime.base_work_end_time,
        base_break_time: data.workTime.base_break_time,
      }
    }
    if (data.breakTime.use_common_setting) {
      param.break_time = {
        use_common_setting: true,
      }
    } else {
      param.break_time = {
        use_common_setting: false,
        break_times: data.breakTime.break_times,
      }
    }
    dispatch(
      updateSettingBaseWorkTime(
        param,
        () => {
          alert.success("個別設定を変更しました")
          setOriginMemberList((cur) => cur.map((v) => (
            data.memberIds.includes(v.id)
              ? {
                ...v,
                work_time: {
                  use_common_setting: param.work_time?.use_common_setting ?? v.work_time.use_common_setting,
                  base_work_start_time: param.work_time?.base_work_start_time ?? v.work_time.base_work_start_time,
                  base_work_end_time: param.work_time?.base_work_end_time ?? v.work_time.base_work_end_time,
                  base_break_time: param.work_time?.base_break_time ?? v.work_time.base_break_time,
                },
                break_time: {
                  use_common_setting: param.break_time?.use_common_setting ?? v.break_time.use_common_setting,
                  break_times: param.break_time?.break_times ?? v.break_time.break_times,
                },
              }
              : { ...v }
          )))
        },
      ),
    )
  }

  useEffect(() => {
    setIsLoading(true)
    dispatch(fetchSettingBaseWorkTime(
      (result) => {
        setOriginMemberList(result.members)
        setCommonValue(result.common)
        setBaseWorkStartTime(timeAdjuster(result.common.base_work_start_time, true) || "")
        setBaseWorkEndTime(timeAdjuster(result.common.base_work_end_time, true) || "")
        setIsBaseWorkTime(!!result.common.base_work_start_time || !!result.common.base_work_end_time)
        setBaseBreakTime(result.common.base_break_time)
        setScheduledBreakTimes(result.common.break_times)
        setScheduledBreakTimes(result.common.break_times.map((v) => ({
          start_time: timeAdjuster(v.start_time, true) || "",
          end_time: timeAdjuster(v.end_time, true) || "",
        })) || [])
        setIsScheduledBreakTimes(!!result.common.break_times.length)
        setIsLoading(false)
      },
    ))
  }, [])

  useEffect(() => {
    const tmpErrors: Error[] = []
    if (baseWorkStartTime && baseWorkEndTime && Number(pickNumber(baseWorkStartTime)) >= Number(pickNumber(baseWorkEndTime))) {
      tmpErrors.push({
        type: "work_time",
        message: "標準勤務時間の設定が誤っています。勤務終了時間は勤務開始時間より前の時間を指定してください。",
      })
    }
    if (isBaseWorkTime && (workTimeSeconds - baseBreakTime <= 0)) {
      tmpErrors.push({
        type: "work_time",
        message: "標準勤務時間の設定が誤っています。休憩予定時間は勤務時間以下にしてください。",
      })
    }
    scheduledBreakTimes.forEach((v, i) => {
      const startNum = Number(pickNumber(v.start_time))
      const endNum = Number(pickNumber(v.end_time))
      if (isScheduledBreakTimes && (endNum - startNum <= 0)) {
        tmpErrors.push({
          type: "break_time",
          message: "標準休憩時間の設定が誤っています。休憩予定終了時間は休憩予定開始時間より前の時間を指定してください。",
        })
      }
      scheduledBreakTimes.filter((_, j) => j > i).forEach((w) => {
        const tmpStartNum = Number(pickNumber(w.start_time))
        const tmpEndNum = Number(pickNumber(w.end_time))
        if (!(startNum >= tmpEndNum || endNum <= tmpStartNum)) {
          tmpErrors.push({
            type: "break_time",
            message: "標準休憩時間の設定が誤っています。設定時間が重複しています。",
          })
        }
      })
    })
    if (tmpErrors.length) {
      setErrors(tmpErrors)
    } else {
      setErrors([])
    }
  }, [baseWorkStartTime, baseWorkEndTime, baseBreakTime, scheduledBreakTimes, workTimeSeconds])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - 標準勤務時刻・休憩時刻設定`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.SETTING_COMMON.path, language)}
        >
          {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
        </Link>
        &nbsp;&gt;&nbsp;
        標準勤務時刻・休憩時刻設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>標準勤務時刻・休憩時刻設定</h1>
      </section>
      <section className="page_description">
        <p>
          勤務開始・終了時間、休憩開始・終了時間を設定することができます。
        </p>
      </section>
      {
        isLoading ? (
          <LoadingSquare>
            <>
              情報を取得しています…
            </>
          </LoadingSquare>
        ) : (
          <>
            {
              login.role_target_restriction || (
                <section className="page_contents content-area">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        会社全体の設定
                      </div>
                    </div>
                    <div className="block-detail">
                      <div
                        style={{
                          display: "flex",
                          flexFlow: "column",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            position: "relative",
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            backgroundColor: "#f3feff",
                            padding: "16px",
                            display: "flex",
                            flexFlow: "column",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              backgroundColor: "#f3f3f3",
                              padding: "8px",
                              margin: "-16px -16px 0 -16px",
                              borderRadius: "4px 4px 0 0",
                              marginBottom: "8px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              勤務時間設定
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexFlow: "column",
                              gap: "4px",
                              width: "100%",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  userSelect: "none",
                                  cursor: "pointer",
                                }}
                                onClick={changeSettingBaseWorkTime}
                                role="none"
                              >
                                {buildCheckBox(isBaseWorkTime)}
                                標準勤務時間を設定する
                              </div>
                            </div>
                            <div
                              style={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "8px",
                                border: "solid 1px #CCCCCC",
                                borderRadius: "4px",
                                padding: "4px 8px",
                                backgroundColor: "#FFFFFF",
                                fontSize: "11px",
                                minHeight: "35px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                勤務開始
                                {buildWorkTimeSelectBox(baseWorkStartTime, (v) => setBaseWorkStartTime(v.toString()))}
                                ~
                                勤務終了
                                {buildWorkTimeSelectBox(baseWorkEndTime, (v) => setBaseWorkEndTime(v.toString()))}
                                うち休憩予定時間
                                {buildBreakTimeSelectBox(baseBreakTime, (v) => setBaseBreakTime(Number(v)))}
                              </div>
                              {
                                !isBaseWorkTime ? (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      width: "100%",
                                      height: "100%",
                                      backgroundColor: "rgba(243, 243, 243, 0.8)",
                                      borderRadius: "4px",
                                      zIndex: "2000",
                                    }}
                                  />
                                ) : (
                                  <>
                                    {
                                      !errors.filter((v) => v.type === "work_time").length && (
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "4px",
                                            color: "#999999",
                                          }}
                                        >
                                          標準勤務時間
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: "4px",
                                            }}
                                          >
                                            {buildSecToTime(baseBreakTime ? workTimeSeconds - baseBreakTime : workTimeSeconds)}
                                            {!!baseBreakTime && `(休憩予定時間:${buildSecToTime(baseBreakTime)})`}
                                          </div>
                                        </div>
                                      )
                                    }
                                  </>
                                )
                              }
                            </div>
                          </div>
                          {
                            errors.filter((v) => v.type === "work_time").map((v) => (
                              <div
                                style={{
                                  border: "solid 1px #FF0000",
                                  borderRadius: "4px",
                                  color: "#FF0000",
                                  backgroundColor: "#fff3f3",
                                  padding: "8px",
                                }}
                              >
                                {v.message}
                              </div>
                            ))
                          }
                        </div>
                        <div
                          style={{
                            position: "relative",
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            backgroundColor: "#f3fff4",
                            padding: "16px",
                            display: "flex",
                            flexFlow: "column",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              backgroundColor: "#f3f3f3",
                              padding: "8px",
                              margin: "-16px -16px 0 -16px",
                              borderRadius: "4px 4px 0 0",
                              marginBottom: "8px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              休憩時間をあらかじめ登録する
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexFlow: "column",
                              gap: "4px",
                              width: "100%",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  userSelect: "none",
                                  cursor: "pointer",
                                }}
                                onClick={changeSettingScheduledBreakTime}
                                role="none"
                              >
                                {buildCheckBox(isScheduledBreakTimes)}
                                休憩時間をあらかじめ登録する
                              </div>
                            </div>
                            <div
                              style={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "8px",
                                border: "solid 1px #CCCCCC",
                                borderRadius: "4px",
                                padding: "4px 8px",
                                backgroundColor: "#FFFFFF",
                                fontSize: "11px",
                                minHeight: "35px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexFlow: "column",
                                    gap: "4px",
                                  }}
                                >
                                  {
                                    scheduledBreakTimes.map((v, i) => (
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "4px",
                                        }}
                                      >
                                        休憩開始
                                        { /* eslint-disable-next-line max-len */}
                                        {buildWorkTimeSelectBox(v.start_time, (value) => setScheduledBreakTimes((cur) => cur.map((w, j) => (i === j ? { ...w, start_time: value.toString() } : w))))}
                                        ~
                                        休憩終了
                                        { /* eslint-disable-next-line max-len */}
                                        {buildWorkTimeSelectBox(v.end_time, (value) => setScheduledBreakTimes((cur) => cur.map((w, j) => (i === j ? { ...w, end_time: value.toString() } : w))))}
                                        {
                                          !!v.start_time && (
                                            <button
                                              type="button"
                                              onClick={() => delBreakTime(i)}
                                              style={{
                                                color: "#FF0000",
                                              }}
                                            >
                                              <FontAwesomeIcon icon={['fas', 'trash']} />
                                            </button>
                                          )
                                        }
                                      </div>
                                    ))
                                  }
                                </div>
                              </div>
                              {
                                !isScheduledBreakTimes ? (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      width: "100%",
                                      height: "100%",
                                      backgroundColor: "rgba(243, 243, 243, 0.8)",
                                      borderRadius: "4px",
                                      zIndex: "2000",
                                    }}
                                  />
                                ) : (
                                  <>
                                    {
                                      !errors.filter((v) => v.type === "break_time").length && (
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "4px",
                                            color: "#999999",
                                          }}
                                        >
                                          合計
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: "4px",
                                            }}
                                          >
                                            {buildSecToTime(scheduledBreakTimeSeconds)}
                                          </div>
                                        </div>
                                      )
                                    }
                                  </>
                                )
                              }
                            </div>
                            {
                              isScheduledBreakTimes && scheduledBreakTimes.length < BREAK_TIME_LIMIT && (
                                <button
                                  type="button"
                                  onClick={addBreakTime}
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: "4px",
                                    border: "solid 1px #008800",
                                    borderRadius: "4px",
                                    color: "#008800",
                                    backgroundColor: "#e1ffe0",
                                  }}
                                >
                                  <FontAwesomeIcon icon={['fas', 'plus']} />
                                  休憩時間を追加
                                </button>
                              )
                            }
                          </div>
                          {
                            errors.filter((v) => v.type === "break_time").map((v) => (
                              <div
                                style={{
                                  border: "solid 1px #FF0000",
                                  borderRadius: "4px",
                                  color: "#FF0000",
                                  backgroundColor: "#fff3f3",
                                  padding: "8px",
                                }}
                              >
                                {v.message}
                              </div>
                            ))
                          }
                        </div>
                      </div>
                      <button
                        type="button"
                        className="mt-4 button"
                        onClick={submit}
                        disabled={!!errors.length}
                      >
                        設定する
                      </button>
                    </div>
                  </div>
                </section>
              )
            }
            <BaseWorkTimeMemberListModule
              originMemberList={originMemberList.map((v) => ({ ...v, use_common_setting: true, value: true }))}
              commonValue={commonValue}
              modalOpenCallback={(memberIds) => openUpdateModal(memberIds)}
            />
            <SettingBaseWorkTimeModal
              targetMemberList={targetMembers}
              isDisplayModal={isDisplayModal}
              submitCallback={(data: { memberIds: number[], workTime: WorkTime, breakTime: BreakTime }) => changeBaseWorkTime(data)}
              closeCallback={closeModal}
            />
          </>
        )
      }
    </section>
  )
}

export default Index
